import React, { useState } from "react";
import { Card, Avatar, Badge } from "components/ui";
import { colorFromText } from "utils/colorFromText";
import { formatBytes } from "utils/formatBytes";
import {
  getFileIcon,
  getInitials,
  formatDate,
  getDocumentUrl,
} from "../utils/documentUtils";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import {
  EllipsisHorizontalIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { Fragment } from "react";
import clsx from "clsx";

import { ViewDocumentModal } from "./ViewDocumentModal";

const DocumentHistoryCard = ({ document }) => {

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const { Icon, colorClass } = getFileIcon(document.FileExtension?.replace('.', '') || 'file');

  // Safe formatting function for file size
  const safeFormatBytes = (bytes) => {
    try {
      if (typeof bytes !== "number" || isNaN(bytes) || bytes < 0) {
        return "Unknown size";
      }
      return formatBytes(bytes);
    } catch (error) {
      console.warn("Error formatting bytes:", error);
      return "Unknown size";
    }
  };

  const handleViewDocument = () => {
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
  };

  const handleOpenInNewTab = () => {
    const filePath = document?.FilePath || document?.filePath;
    window.open(getDocumentUrl(filePath), "_blank");
  };

  const getActionTypeColor = (actionType) => {
    switch (actionType) {
      case 1: // Verified
        return "success";
      case 2: // Rejected
        return "error";
      case 3: // Final Approved
        return "primary";
      default:
        return "neutral";
    }
  };

  const getActionTypeBgColor = (actionType) => {
    switch (actionType) {
      case 1: // Verified
        return "bg-green-100 dark:bg-green-900/30";
      case 2: // Rejected
        return "bg-red-100 dark:bg-red-900/30";
      case 3: // Final Approved
        return "bg-blue-100 dark:bg-blue-900/30";
      default:
        return "bg-gray-100 dark:bg-gray-900/30";
    }
  };

  const getActionTypeDotColor = (actionType) => {
    switch (actionType) {
      case 1: // Verified
        return "bg-green-500";
      case 2: // Rejected
        return "bg-red-500";
      case 3: // Final Approved
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  };

  const safeFormatDate = (dateString) => {
    try {
      if (!dateString) return "N/A";
      return formatDate(dateString);
    } catch (error) {
      console.warn("Error formatting date:", error);
      return "N/A";
    }
  };

  return (
    <Card className="p-4">
      {/* Document Header */}
      <div className="flex items-start space-x-3 mb-4">
        <div className={`flex-shrink-0 ${colorClass}`}>
          <Icon className="h-8 w-8" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="dark:text-dark-50 truncate text-sm leading-tight font-medium text-gray-900">
                {document.FileName || document.name || 'Unknown Document'}
              </h3>
              <p className="dark:text-dark-400 mt-0.5 text-xs text-gray-500">
                {safeFormatBytes(document.size || document.Size || 0)}
              </p>
            </div>

            {/* Actions Menu */}
            <div className="flex justify-center">
              <Menu as="div" className="relative inline-block text-left">
                <MenuButton
                  as="div"
                  className="dark:text-dark-200 dark:hover:bg-dark-300/10 dark:focus:bg-dark-300/10 dark:active:bg-dark-300/20 inline-flex size-6 cursor-pointer items-center justify-center rounded-full text-gray-700 hover:bg-gray-300/20 focus:bg-gray-300/20 active:bg-gray-300/25 sm:size-7"
                  onClick={(e) => e.stopPropagation()}
                >
                  <EllipsisHorizontalIcon className="size-4" />
                </MenuButton>
                <Transition
                  as={Fragment}
                  enter="transition ease-out"
                  enterFrom="opacity-0 translate-y-2"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-2"
                >
                  <MenuItems
                    modal={false}
                    className="dark:bg-dark-750 dark:border-dark-500 absolute right-0 bottom-full z-50 mt-2 min-w-[10rem] rounded-lg border border-gray-300 bg-white py-1 shadow-lg shadow-gray-200/50 outline-hidden focus-visible:outline-hidden dark:shadow-none"
                  >
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDocument();
                          }}
                          className={clsx(
                            "flex h-9 w-full items-center cursor-pointer space-x-3 px-3 tracking-wide outline-hidden transition-colors",
                            focus &&
                            "dark:bg-dark-600 dark:text-dark-100 bg-gray-100 text-gray-800",
                          )}
                        >
                          <EyeIcon className="size-3.5 stroke-1" />
                          <span className="truncate">View</span>
                        </button>
                      )}
                    </MenuItem>
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenInNewTab();
                          }}
                          className={clsx(
                            "flex h-9 w-full items-center cursor-pointer space-x-3 px-3 tracking-wide outline-hidden transition-colors",
                            focus &&
                            "dark:bg-dark-600 dark:text-dark-100 bg-gray-100 text-gray-800",
                          )}
                        >
                          <ArrowTopRightOnSquareIcon className="size-3.5 stroke-1" />
                          <span className="truncate">Open in New Tab</span>
                        </button>
                      )}
                    </MenuItem>
                  </MenuItems>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>
      </div>

      {/* History Timeline */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Document History
        </h4>

        {document.History && document.History.length > 0 ? (
          <div className="space-y-3">
            {document.History.map((historyItem, index) => (
              <div key={`${historyItem.Version}-${index}`} className="relative">
                {/* Timeline Line */}
                {index < document.History.length - 1 && (
                  <div className="absolute left-4 top-8 bottom-0 w-px bg-gray-200 dark:bg-gray-700" />
                )}

                <div className="flex items-start space-x-3">
                  {/* Timeline Dot */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full ${getActionTypeBgColor(historyItem.ActionType)} flex items-center justify-center`}>
                    <div className={`w-3 h-3 rounded-full ${getActionTypeDotColor(historyItem.ActionType)}`} />
                  </div>

                  {/* History Content */}
                  <div className="min-w-0 flex-1 pb-4">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Badge
                        variant="soft"
                        color={getActionTypeColor(historyItem.ActionType)}
                        className="text-[11px]"
                      >
                        {historyItem.ActionTypeText}
                      </Badge>

                    </div>

                    <div className="flex items-center space-x-2 mt-2">
                      <Avatar
                        size={8}
                        color={colorFromText(historyItem.ActionByName || "Unknown")}
                      >
                        {getInitials(historyItem.ActionByName || "Unknown")}
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="dark:text-dark-300 text-xs leading-tight text-gray-600">
                          {historyItem.ActionByName || "Unknown"}
                        </p>
                        <p className="dark:text-dark-400 text-xs text-gray-500">
                          {safeFormatDate(historyItem.ActionAt)}
                        </p>
                      </div>
                    </div>

                    {historyItem.Remark && (
                      <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-md">
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                          {historyItem.Remark}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No history available for this document.
            </p>
          </div>
        )}
      </div>

      {/* View Document Modal */}
      <ViewDocumentModal
        isOpen={viewModalOpen}
        onClose={handleCloseViewModal}
        document={document}
        onOpenInNewTab={handleOpenInNewTab}
      />
    </Card>
  );
};

export default DocumentHistoryCard;
